import { Injectable, inject } from '@angular/core';
import { GlobalUtilService } from '@otas/core/services/global-util.service';

/**
 * Merge-safe access to the per-machine print-config.properties file.
 *
 * Writes go through the registered Tauri command `write_file_in_path`
 * (the legacy FilesService path is dead under Tauri 2 because
 * GlobalUtilService.fsInstance is null, so its promises never settle).
 *
 * Reads are attempted in order:
 *   1. a dedicated `read_print_config` Tauri command (future-proof — not
 *      registered yet at the time of writing),
 *   2. @tauri-apps/plugin-fs readTextFile (plugin is registered in Rust;
 *      may be denied if no capability grants fs read),
 *   3. a localStorage mirror of the last key map this app successfully
 *      read or wrote on this machine.
 *
 * The mirror keeps merges safe even when the file itself cannot be read:
 * every successful read/write refreshes it, so "read mirror -> apply
 * changed keys -> write full map" never drops keys this app has seen.
 */
export type PrintConfigSource = 'file' | 'mirror' | 'empty';

export interface PrintConfigReadResult {
    map: Record<string, string>;
    source: PrintConfigSource;
}

@Injectable({ providedIn: 'root' })
export class PrintConfigFileService {

    private static readonly MIRROR_STORAGE_KEY = 'PRINT_CONFIG_MIRROR';
    private static readonly FILE_NAME = 'print-config.properties';

    private globalUtilService = inject(GlobalUtilService);

    // ---------- helpers ----------

    isTauri(): boolean {
        return typeof (window as any).__TAURI__ !== 'undefined';
    }

    private async invoke(cmd: string, args?: any): Promise<any> {
        const tauri = await import('@tauri-apps/api/core');
        return tauri.invoke(cmd, args);
    }

    private async configDir(): Promise<string> {
        const fromUtil = this.globalUtilService.configPath;
        if (fromUtil) {
            return fromUtil;
        }
        return await this.invoke('ipc_request', { arg: 'configPath' });
    }

    /** Parses `key=value` lines. Splits on the FIRST '=' only, keeps raw values. */
    parseProperties(text: string): Record<string, string> {
        const map: Record<string, string> = {};
        if (!text) {
            return map;
        }
        for (const rawLine of text.split(/\r?\n/)) {
            const line = rawLine.trim();
            if (!line || line.startsWith('#') || line.startsWith('!')) {
                continue;
            }
            const idx = line.indexOf('=');
            if (idx <= 0) {
                continue;
            }
            map[line.substring(0, idx).trim()] = line.substring(idx + 1);
        }
        return map;
    }

    /** Serializes the key map the same way the legacy writers did: raw `key=value` lines. */
    stringify(map: Record<string, string>): string {
        return Object.keys(map)
            .sort()
            .map(key => `${key}=${map[key] == null ? '' : map[key]}`)
            .join('\n');
    }

    /** Converts FilesService.deflate output (["a.b=c", ...]) into a key map. */
    entriesToMap(entries: string[]): Record<string, string> {
        return this.parseProperties((entries || []).join('\n'));
    }

    // ---------- mirror ----------

    private readMirror(): Record<string, string> | null {
        try {
            const raw = localStorage.getItem(PrintConfigFileService.MIRROR_STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private saveMirror(map: Record<string, string>) {
        try {
            localStorage.setItem(PrintConfigFileService.MIRROR_STORAGE_KEY, JSON.stringify(map));
        } catch {
            // storage full / unavailable — mirror is best-effort
        }
    }

    // ---------- read / write ----------

    async readKeyMap(): Promise<PrintConfigReadResult> {
        if (this.isTauri()) {
            // 1. dedicated command, if/when the Rust side registers one
            try {
                const text: string = await this.invoke('read_print_config');
                const map = this.parseProperties(text);
                this.saveMirror(map);
                return { map, source: 'file' };
            } catch {
                // command not registered — fall through
            }
            // 2. fs plugin (registered in lib.rs; may lack a capability grant)
            try {
                const dir = await this.configDir();
                const fsPlugin = await import('@tauri-apps/plugin-fs');
                const text = await fsPlugin.readTextFile(`${dir}\\${PrintConfigFileService.FILE_NAME}`);
                const map = this.parseProperties(text);
                this.saveMirror(map);
                return { map, source: 'file' };
            } catch {
                // denied or file missing — fall through to mirror
            }
        }
        const mirror = this.readMirror();
        if (mirror) {
            return { map: mirror, source: 'mirror' };
        }
        return { map: {}, source: 'empty' };
    }

    /**
     * Writes the COMPLETE key map. Callers must pass a merged map
     * (existing keys + their changes) — never a partial one.
     */
    async writeKeyMap(map: Record<string, string>): Promise<void> {
        this.saveMirror(map);
        if (!this.isTauri()) {
            return; // web build: mirror only
        }
        const dir = await this.configDir();
        await this.invoke('write_file_in_path', {
            folderPath: dir,
            fileName: PrintConfigFileService.FILE_NAME,
            content: this.stringify(map)
        });
    }

    /**
     * Merge-safe partial update: reads the current full key map, applies only
     * `changes`, writes the complete merged set back.
     * Returns the source the baseline came from so callers can warn the user
     * when the real file was not readable.
     */
    async mergeAndWrite(changes: Record<string, string>): Promise<PrintConfigSource> {
        const existing = await this.readKeyMap();
        const merged = { ...existing.map, ...changes };
        await this.writeKeyMap(merged);
        return existing.source;
    }
}
